import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import Config from '@/config';

import SignatureCapture from './SignatureCapture';
import DocumentViewer from './DocumentViewer';
import SigningBottomBar from './SigningBottomBar';
import SigningSuccess from './SigningSuccess';
import SigningError from './SigningError';

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (data: string) => void };
  }
}

interface DocumentItem {
  id: number;
  name: string;
  version_id: number;
  download_url: string | null;
  signed: boolean;
  locked: boolean;
}

type FlowState =
  | 'loading'
  | 'signature_capture'
  | 'document_view'
  | 'success'
  | 'error';
type ErrorType = 'expired' | 'not_found' | 'network' | null;

const QA_SAMPLE_PDF =
  'https://s3.us-east-1.amazonaws.com/web-qa.gravywork.com/sample.pdf';

const QA_DOCUMENTS: DocumentItem[] = [
  {
    id: 1,
    name: 'Employee Handbook',
    version_id: 5,
    download_url: QA_SAMPLE_PDF,
    signed: false,
    locked: false,
  },
  {
    id: 2,
    name: 'Arbitration Agreement',
    version_id: 8,
    download_url: QA_SAMPLE_PDF,
    signed: false,
    locked: false,
  },
  {
    id: 3,
    name: 'Safety Policy',
    version_id: 12,
    download_url: QA_SAMPLE_PDF,
    signed: false,
    locked: false,
  },
];

const BASE_URL = Config.BASE_RUBY_URL || 'https://api.v2.gravywork.com';

const DocumentSigning: React.FC = () => {
  const [searchParams] = useSearchParams();

  const authTokenRef = useRef<string>('');
  const isQaModeRef = useRef(false);

  const [flowState, setFlowState] = useState<FlowState>('loading');
  const [errorType, setErrorType] = useState<ErrorType>(null);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [currentDocId, setCurrentDocId] = useState<number | null>(null);
  const [signatureBase64, setSignatureBase64] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);

  const documentId = searchParams.get('document_id');

  const unsignedDocs = documents.filter((d) => !d.signed && !d.locked);
  const signedCount = documents.filter((d) => d.signed).length;
  const totalCount = documents.filter((d) => !d.locked).length;
  const currentDoc = documents.find((d) => d.id === currentDocId) || null;
  const currentUnsignedIndex = unsignedDocs.findIndex(
    (d) => d.id === currentDocId,
  );
  const isLastDocument = currentUnsignedIndex === unsignedDocs.length - 1;

  const fetchDocuments = useCallback(async () => {
    setFlowState('loading');

    if (isQaModeRef.current) {
      setDocuments(QA_DOCUMENTS.map((d) => ({ ...d })));
      return QA_DOCUMENTS;
    }

    try {
      const response = await fetch(`${BASE_URL}/m/documents`, {
        headers: {
          Authorization: `bearer ${authTokenRef.current}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          setErrorType('expired');
          setFlowState('error');
          return null;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        setErrorType('network');
        setFlowState('error');
        return null;
      }

      setDocuments(data.data.documents);
      return data.data.documents;
    } catch {
      setErrorType('network');
      setFlowState('error');
      return null;
    }
  }, []);

  const initialize = useCallback(async () => {
    const token = searchParams.get('token') || '';
    const docId = searchParams.get('document_id');

    authTokenRef.current = token;
    isQaModeRef.current = token === 'qa_testing';

    // Strip token from URL
    const newUrl =
      window.location.pathname +
      '?document_id=' +
      (docId || '') +
      '&source=mobile';
    window.history.replaceState({}, '', newUrl);

    if (!token) {
      setErrorType('expired');
      setFlowState('error');
      return;
    }

    const docs = await fetchDocuments();
    if (!docs) return;

    if (docId) {
      const targetDoc = docs.find(
        (d: DocumentItem) => d.id === parseInt(docId, 10),
      );
      if (!targetDoc) {
        setErrorType('not_found');
        setFlowState('error');
        return;
      }
      if (targetDoc.signed) {
        const allSigned = docs.every((d: DocumentItem) => d.signed || d.locked);
        if (allSigned) {
          setFlowState('success');
        } else {
          // Find next unsigned document
          const nextUnsigned = docs.find(
            (d: DocumentItem) => !d.signed && !d.locked,
          );
          if (nextUnsigned) {
            setCurrentDocId(nextUnsigned.id);
            setFlowState('signature_capture');
          } else {
            setFlowState('success');
          }
        }
        return;
      }
      setCurrentDocId(targetDoc.id);
    } else {
      // No specific doc — find the first unsigned
      const firstUnsigned = docs.find(
        (d: DocumentItem) => !d.signed && !d.locked,
      );
      if (!firstUnsigned) {
        setFlowState('success');
        return;
      }
      setCurrentDocId(firstUnsigned.id);
    }

    setFlowState('signature_capture');
  }, [searchParams, fetchDocuments]);

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignatureCapture = useCallback((base64: string) => {
    setSignatureBase64(base64);
    setFlowState('document_view');
  }, []);

  const handleChangeSignature = useCallback(() => {
    setFlowState('signature_capture');
  }, []);

  const handleSign = useCallback(async () => {
    if (!currentDoc || !signatureBase64) return;

    setSigning(true);

    try {
      if (isQaModeRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } else {
        const response = await fetch(
          `${BASE_URL}/m/documents/${currentDoc.id}/sign`,
          {
            method: 'POST',
            headers: {
              Authorization: `bearer ${authTokenRef.current}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ signature_image: signatureBase64 }),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (!data.success) {
          throw new Error('Sign failed');
        }
      }

      // Mark document as signed locally
      setDocuments((prev) =>
        prev.map((d) => (d.id === currentDoc.id ? { ...d, signed: true } : d)),
      );

      const newSignedCount = signedCount + 1;
      const allSignedNow = newSignedCount >= totalCount;

      // Post message to app
      if (allSignedNow) {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({
            type: 'document_signing_complete',
            all_signed: true,
          }),
        );
      } else {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({
            type: 'document_signed',
            document_id: currentDoc.id,
            signed_count: newSignedCount,
            total_count: totalCount,
            all_signed: false,
          }),
        );
      }

      if (allSignedNow) {
        setFlowState('success');
      } else {
        // Move to next unsigned document
        const remaining = unsignedDocs.filter((d) => d.id !== currentDoc.id);
        if (remaining.length > 0) {
          setCurrentDocId(remaining[0].id);
          // Stay in document_view for the next doc
        } else {
          setFlowState('success');
        }
      }
    } catch {
      setErrorType('network');
      setFlowState('error');
    } finally {
      setSigning(false);
    }
  }, [currentDoc, signatureBase64, signedCount, totalCount, unsignedDocs]);

  const handleDone = useCallback(() => {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'document_signing_complete',
        all_signed: true,
      }),
    );
  }, []);

  const handleRetry = useCallback(() => {
    setErrorType(null);
    initialize();
  }, [initialize]);

  return (
    <div style={styles.root}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {flowState === 'loading' && (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Loading documents...</p>
        </div>
      )}

      {flowState === 'signature_capture' && (
        <SignatureCapture onCapture={handleSignatureCapture} />
      )}

      {flowState === 'document_view' && currentDoc && signatureBase64 && (
        <div style={styles.documentViewContainer}>
          {currentDoc.name && (
            <div style={styles.docHeader}>
              <span style={styles.docTitle}>{currentDoc.name}</span>
            </div>
          )}
          <div style={styles.viewerArea}>
            <DocumentViewer pdfUrl={currentDoc.download_url || ''} />
          </div>
          <SigningBottomBar
            signaturePreview={signatureBase64}
            onChangeSignature={handleChangeSignature}
            onSign={handleSign}
            signing={signing}
            signedCount={signedCount}
            totalCount={totalCount}
            isLastDocument={isLastDocument}
          />
        </div>
      )}

      {flowState === 'success' && <SigningSuccess onDone={handleDone} />}

      {flowState === 'error' && errorType && (
        <SigningError
          type={errorType}
          onRetry={errorType === 'network' ? handleRetry : undefined}
        />
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  root: {
    height: '100dvh',
    overflow: 'hidden',
    overscrollBehavior: 'none',
    WebkitOverflowScrolling: 'touch',
    WebkitUserSelect: 'none',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    margin: 0,
    padding: 0,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f9fafb',
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid #d1d5db',
    borderTopColor: '#2563eb',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 15,
    color: '#6b7280',
  },
  documentViewContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  docHeader: {
    padding: '12px 20px',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e5e5e5',
  },
  docTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#111827',
  },
  viewerArea: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 130, // space for bottom bar
  },
};

export default DocumentSigning;
